import React, { useState } from "react";
import { Card, Tooltip } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAvatar from "components/SuiAvatar";
import { Add as AddIcon } from "@mui/icons-material";
import moment from "moment";

const AddNewSchedule = ({
  dietPlan,
  defaultMealSchedules,
  customSchedules,
  setActiveSchedule,
  onDeleteSchedule,
  activeSchedule,
  setScheduleModalOpen,
  onUpdateTime,
}) => {
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [tempTime, setTempTime] = useState(""); // Holds temporary edited time

  const handleTimeChange = (scheduleKey, value) => {
    setTempTime(value);
  };

  const saveEditedTime = (scheduleKey) => {
    onUpdateTime(scheduleKey, tempTime);
    setEditingTimeId(null);
  };

  return (
    <Card sx={{ mb: 1, p: 2, borderRadius: "10px" }}>
      <SuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <SuiBox
          sx={{
            display: "flex",
            overflowX: "auto",
            pb: 2.2,
          }}
        >
          {Object.keys(dietPlan)
            .sort((a, b) => {
              const findTime = (scheduleId) => {
                const defaultTime = defaultMealSchedules.find(
                  (s) => s.id === scheduleId,
                )?.time;

                const customTime = customSchedules.find(
                  (s) => s.normalizedId === scheduleId,
                )?.time;

                return defaultTime || customTime || "00:00";
              };

              const timeA = findTime(a);
              const timeB = findTime(b);

              return moment(timeA, "HH:mm").diff(moment(timeB, "HH:mm"));
            })
            .map((scheduleKey) => {
              const schedule =
                defaultMealSchedules.find((s) => s.id === scheduleKey) ||
                customSchedules.find((s) => s.normalizedId === scheduleKey);

              return (
                <SuiBox
                  key={scheduleKey}
                  onClick={() => {
                    setActiveSchedule(scheduleKey);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    px: 2,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "grey.300" },
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <SuiBox
                    onClick={(e) => onDeleteSchedule(e, scheduleKey)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "#f44336",
                      color: "#fff",
                      borderRadius: "50%",
                      width: 15,
                      height: 15,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "12px",
                      "&:hover": { backgroundColor: "#d32f2f" },
                    }}
                  >
                    -
                  </SuiBox>
                  <SuiTypography sx={{ fontSize: "30px" }}>
                    {schedule?.icon?.startsWith("http") ? (
                      <SuiBox
                        component="img"
                        sx={{
                          objectFit: "contain",
                          borderRadius: 2,
                          width: 30,
                          height: 30,
                        }}
                        src={schedule?.icon}
                        alt={schedule.title}
                      />
                    ) : (
                      schedule?.icon || "🍽️"
                    )}
                  </SuiTypography>
                  <SuiTypography
                    variant="subtitle2"
                    sx={{
                      textAlign: "center",
                      color:
                        activeSchedule === scheduleKey ? "#F96800" : "#606060",
                      fontSize: "14px",
                      textTransform: "capitalize",
                    }}
                  >
                    {schedule?.title}
                  </SuiTypography>
                  {editingTimeId === scheduleKey ? (
                    <input
                      type="time"
                      value={tempTime || schedule?.time}
                      onChange={(e) =>
                        handleTimeChange(scheduleKey, e.target.value)
                      }
                      onBlur={() => saveEditedTime(scheduleKey)}
                      autoFocus
                      style={{
                        fontSize: "12px",
                        padding: "2px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                  ) : (
                    <Tooltip title={"Set a new time"} arrow placement="top">
                      <SuiTypography
                        variant="caption"
                        sx={{
                          color:
                            activeSchedule === scheduleKey
                              ? "#F96800"
                              : "text.secondary",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "scale(1.04)", // Slight zoom on hover
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTimeId(scheduleKey);
                          setTempTime(schedule?.time);
                        }}
                      >
                        {moment(schedule?.time, "HH:mm").format("hh:mm A")}
                      </SuiTypography>
                    </Tooltip>
                  )}
                </SuiBox>
              );
            })}
        </SuiBox>

        <SuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            p: 1,
            cursor: "pointer",
            transition: "background-color 0.2s",
            "&:hover": { bgcolor: "grey.300" },
          }}
          onClick={() => setScheduleModalOpen(true)}
        >
          <SuiAvatar
            sx={{
              backgroundColor: "#003264",
              height: "25px",
              width: "25px",
            }}
          >
            <AddIcon />
          </SuiAvatar>

          <SuiTypography
            color="text.secondary"
            variant="caption"
            align="center"
            lineHeight={1.3}
            mt={1}
            sx={{ fontWeight: 600 }}
          >
            Add Schedule
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default AddNewSchedule;
